import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Alert,
} from '@mui/material';
import {
  ExpandMore,
  Gavel,
  EmojiEvents,
  TrendingUp,
  People,
  SportsMma,
  Schedule,
  FitnessCenter,
  Sports,
  ArrowUpward,
  ArrowDownward,
  CheckCircle,
  Cancel,
  WorkspacePremium,
  Share,
  Event,
} from '@mui/icons-material';

const RulesGuidelines: React.FC = () => {
  // Rules/Guidelines component for Creative Fighter Club
  const [expanded, setExpanded] = useState<string | false>('introduction');

  // Ensure component is loaded in production
  React.useEffect(() => {
    // Log component mount for debugging
    console.log('RulesGuidelines component mounted', { 
      env: process.env.NODE_ENV,
      timestamp: new Date().toISOString()
    });
  }, []);

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  // Force component to be included in production build
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      // Ensure component is loaded
      (window as any).__RULES_GUIDELINES_LOADED__ = true;
      console.log('RulesGuidelines component initialized - window flag set');
    }
  }, []);

  return (
    <>
      {/* Full-screen background layer */}
      <Box
        component="div"
        sx={{
          position: 'fixed',
          top: 0,
          left: { xs: 0, sm: '240px' },
          right: 0,
          bottom: 0,
          width: { xs: '100%', sm: 'calc(100% - 240px)' },
          height: '100vh',
          backgroundImage: 'url("/AdobeStock_265779582.jpeg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          zIndex: -1,
          display: 'block',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            pointerEvents: 'none',
            zIndex: 1,
          },
        }}
      />
      {/* Content layer */}
      <Container 
        maxWidth="lg" 
        sx={{ 
          py: 4, 
          position: 'relative', 
          zIndex: 0,
          '& .MuiAccordion-root': {
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            mb: 2,
          },
          '& .MuiPaper-root': {
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          },
        }}
      >
        {/* Header */}
        <Paper elevation={3} sx={{ p: 4, mb: 4, background: 'linear-gradient(135deg, rgba(211, 47, 47, 0.95) 0%, rgba(154, 0, 7, 0.95) 100%)', backdropFilter: 'blur(10px)' }}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Gavel sx={{ fontSize: 40, color: 'white' }} />
          <Box>
            <Typography variant="h3" component="h1" color="white" fontWeight="bold">
              Tantalus Boxing Club – Creative Fighter Club
            </Typography>
            <Typography variant="h6" color="rgba(255,255,255,0.9)" sx={{ mt: 1 }}>
              Official Rules & Guidelines (v1.1.3)
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={3} flexWrap="wrap" mt={2}>
          <Chip 
            label={`Last Updated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`} 
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} 
          />
          <Chip 
            label="Governing Body: Tantalus Boxing Club (TBC)" 
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} 
          />
          <Chip 
            label="Club Name: Creative Fighter Club (CFC)" 
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} 
          />
        </Box>
      </Paper>

      {/* Table of Contents */}
      <Paper elevation={2} sx={{ p: 3, mb: 4, bgcolor: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)' }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Table of Contents
        </Typography>
        <List dense>
          {[
            { id: 'introduction', text: 'Introduction', icon: <People /> },
            { id: 'appendix-quick-reference', text: 'Appendix: Quick Reference', icon: <CheckCircle /> },
            { id: 'boxing-sanctions', text: 'Boxing Sanctions System', icon: <WorkspacePremium /> },
            { id: 'callout-rematch-system', text: 'Callout/Rematch System', icon: <SportsMma /> },
            { id: 'code-of-conduct', text: 'Code of Conduct', icon: <Gavel /> },
            { id: 'demotion-promotion-system', text: 'Demotion and Promotion System', icon: <ArrowUpward /> },
            { id: 'fight-scheduling-rules', text: 'Fight Scheduling Rules', icon: <Schedule /> },
            { id: 'general-guidelines', text: 'General Guidelines', icon: <People /> },
            { id: 'matchmaking-rules', text: 'Matchmaking Rules', icon: <SportsMma /> },
            { id: 'points-system', text: 'Points System', icon: <EmojiEvents /> },
            { id: 'rankings-system', text: 'Rankings System', icon: <TrendingUp /> },
            { id: 'record-your-fights', text: 'Record Your Fights', icon: <Schedule /> },
            { id: 'simulation-standard', text: 'Simulation Virtual Boxing Standard (Undisputed)', icon: <Gavel /> },
            { id: 'tantalus-ring-media', text: 'Tantalus Ring Magazine Media', icon: <Share /> },
            { id: 'tier-system', text: 'Tier System', icon: <TrendingUp /> },
            { id: 'tbc-promotions-fight-calendar', text: 'TBC Promotions Fight Calendar', icon: <Event /> },
            { id: 'tournament-rules', text: 'Tournament Rules', icon: <Sports /> },
            { id: 'training-camp-system', text: 'Training Camp System', icon: <FitnessCenter /> },
          ].sort((a, b) => {
            // Keep Introduction at the top
            if (a.id === 'introduction') return -1;
            if (b.id === 'introduction') return 1;
            // Sort all others alphabetically by text
            return a.text.localeCompare(b.text);
          }).map((item) => (
            <ListItem 
              key={item.id}
              disablePadding
              sx={{ mb: 0.5 }}
            >
              <ListItemButton
                onClick={() => {
                  setExpanded(item.id);
                  document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                sx={{ borderRadius: 1 }}
              >
                <ListItemText 
                  primary={item.text}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Introduction */}
      <Accordion expanded={expanded === 'introduction'} onChange={handleChange('introduction')} id="introduction">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2}>
            <People color="primary" />
            <Typography variant="h5" fontWeight="bold">Introduction</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" paragraph>
            Welcome to the <strong>Tantalus Boxing Club – Creative Fighter Club (TBC‑CFC)</strong>: a competitive Undisputed club where created fighters advance through tiers, compete in tournaments, and build their legacy through skill, discipline, and fairness.
          </Typography>
          <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>Core Principles</Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Fair Competition"
                secondary="Matches are driven by skill, tier, and ranking integrity."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Progressive Advancement"
                secondary="Fighters rise from Amateur to Elite through performance."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Respect & Sportsmanship"
                secondary="Conduct yourself as a professional inside and outside the ring."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Transparency"
                secondary="Rankings, points, and match outcomes are visible to the community."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Tiered Progression"
                secondary="Boxing Sanctions unlock based on your points, rewarding consistent performance and advancement."
              />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>How the Application Works</Typography>
          <Typography variant="body2" paragraph>
            The Tantalus Boxing Club application is a comprehensive platform for managing your boxing career:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="1. Create Your Fighter Profile"
                secondary="Set up your fighter with stats, weight class, platform, and timezone. Complete your profile to access all features."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="2. Earn Points Through Fights"
                secondary="Win fights to earn points (+5 for win, +8 for KO/TKO). Points determine your tier and unlock Boxing Sanctions."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="3. Advance Through Tiers"
                secondary="Start at Amateur (0-29 pts), progress to Semi-Pro (30-69), Pro (70-139), Contender (140-279), Elite (280-559), and Hall of famer (560+)."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="4. Unlock Boxing Sanctions"
                secondary="As you earn points, new Boxing Sanctions become available. Join sanctions to compete for rankings within each governing body."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="5. Compete in Tournaments"
                secondary="Enter tournaments based on your tier and weight class. Win tournaments to earn recognition and advance your career."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="6. Use Training Camps"
                secondary="Practice with other fighters in training camps. No points at stake, perfect for skill development and strategy testing."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="7. Track Your Progress"
                secondary="View your rankings, fight history, analytics, and progress through the tier system. Monitor your position in each Boxing Sanction."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="8. Create Your Media Profile"
                secondary="Add a social media bio and links in My Profile → Physical Information. Your profile appears in Tantalus Ring Magazine Media Following for others to discover."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="9. Share Your Media Profile"
                secondary="Copy your shareable media link to share with fans, sponsors, or on social media. Public can view your media profile without logging in."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="10. Stay Connected"
                secondary="Receive notifications for pending sanctions, active sanctions, fight requests, tournaments, new fighters, and important updates."
              />
            </ListItem>
          </List>
        </AccordionDetails>
      </Accordion>

      {/* Appendix: Quick Reference */}
      <Accordion expanded={expanded === 'appendix-quick-reference'} onChange={handleChange('appendix-quick-reference')} id="appendix-quick-reference">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2}>
            <CheckCircle color="primary" />
            <Typography variant="h5" fontWeight="bold">Appendix: Quick Reference</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell><strong>Point Values</strong></TableCell>
                  <TableCell>Win +5 | KO/TKO +8 | Loss −3 | Draw 0</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Tier Thresholds</strong></TableCell>
                  <TableCell>Amateur 0–29 | Semi‑Pro 30–69 | Pro 70–139 | Contender 140–279 | Elite 280–559 | Hall of famer 560+</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Matchmaking</strong></TableCell>
                  <TableCell>Same weight/tier | Rank within 3 | Points within 30 | Timezones within 4h</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Demotion</strong></TableCell>
                  <TableCell>5 consecutive losses → drop 1 tier | Re‑promotion: 5 consecutive wins</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Boxing Sanctions</strong></TableCell>
                  <TableCell>Unlock based on points (0-29, 30-69, 70-139, 140-279, 280-559, 560+) | Ranked by wins/losses | Notifications for pending/active</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Scheduling</strong></TableCell>
                  <TableCell>Mandatory: complete within 7 days | Scheduled fights: up to 4 weeks ahead | Camps: 72 hours; none within 3 days of a fight</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Sim Highlights</strong></TableCell>
                  <TableCell>Pace 35–70/rd | ≥20% jabs | No spam | 3 KD Rule | Fouls/cuts on | 10‑Point Must | Macros banned</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Tantalus Ring Media</strong></TableCell>
                  <TableCell>Social media bio & links in My Profile | Shareable media page (/media/{'{user_id}'}) | Media Following channel in Social page</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" gutterBottom>Versioning & Changelog</Typography>
          <Typography variant="body2" paragraph>
            <strong>v1.1.3 ({new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}):</strong> Automatic Loss System & Tier Updates - Fixed automatic opponent loss system: when a fighter enters a win, the opponent now automatically receives a loss record with -3 points. Both fighters' rankings, demotions, tier, and points update in real-time across all systems. Added Hall of famer tier (560+ points). Updated tier thresholds: Amateur (0-29), Semi-Pro (30-69), Pro (70-139), Contender (140-279), Elite (280-559), Hall of famer (560+). All systems now update in real-time: Home Page, Rankings, Weight Class, Demotion System, Tier System, Smart Matchmaking, and Analytics.
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>v1.1.2 ({new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}):</strong> Tantalus Ring Magazine Media Feature - Added social media bio and links management in fighter profiles. Created shareable media profile pages (/media/{'{user_id}'}) for public viewing. Added "Tantalus Ring Magazine Media Following" sub-channel in Social page to browse fighters with media profiles. Fixed notification system navigation (New Fighter Joined and Boxing Sanction notifications now navigate correctly). Performance optimizations (notification handlers 90% faster, HomePage input delays reduced). Fixed news announcements 500 error. Enhanced error handling and user experience throughout.
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>v1.1.1 (2025-01-XX):</strong> Added Boxing Sanctions System - fighters can now join governing bodies and compete for rankings within each sanction. Updated points system (Loss: -3), demotion system (5 consecutive losses), enhanced visual design, larger belt images, and comprehensive feature documentation.
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>v1.1.0 (2025-01-XX):</strong> Creative Fighter images, enhanced rankings display, fight URL and scorecard submissions, real-time updates, and visual improvements.
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>v1.0.0 (2025‑11‑16):</strong> Initial TBC‑CFL consolidation: tiers, points, rankings, matchmaking, tournaments, camps, callouts, scheduling, promotion/demotion, sim standard, conduct, and quick reference.
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* Boxing Sanctions System */}
      <Accordion expanded={expanded === 'boxing-sanctions'} onChange={handleChange('boxing-sanctions')} id="boxing-sanctions">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2}>
            <WorkspacePremium color="primary" />
            <Typography variant="h5" fontWeight="bold">Boxing Sanctions System</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" paragraph>
            The <strong>Boxing Sanctions System</strong> is a tiered unlock system where fighters must earn points to unlock access to different governing bodies. Each sanction maintains its own rankings, and fighters compete for position within their chosen sanctions.
          </Typography>

          <Alert severity="info" sx={{ mt: 2, mb: 3 }}>
            <strong>New in v1.2.0:</strong> Sanctions are now unlocked based on your fighter's points. You'll receive notifications when new sanctions become available!
          </Alert>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Sanction Unlock System</Typography>
          <Typography variant="body2" paragraph>
            Sanctions are unlocked progressively as you advance through tiers. Each sanction has specific point requirements:
          </Typography>
          <TableContainer component={Paper} sx={{ mt: 2, mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Sanction</strong></TableCell>
                  <TableCell><strong>Points Required</strong></TableCell>
                  <TableCell><strong>Tier</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell><strong>TBCA</strong></TableCell>
                  <TableCell>0-29 pts</TableCell>
                  <TableCell>Amateur</TableCell>
                  <TableCell>Tantalus Boxing Club Amateur Association - Governing amateur bouts and club-level competitions</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>TBA</strong></TableCell>
                  <TableCell>30-69 pts</TableCell>
                  <TableCell>Semi-Pro</TableCell>
                  <TableCell>Tantalus Boxing Association - Oversees regional events and standardized amateur rankings</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>TBO</strong></TableCell>
                  <TableCell>70-139 pts</TableCell>
                  <TableCell>Pro</TableCell>
                  <TableCell>Tantalus Boxing Organization - Professional-level sanctioning body for title fights and promotions</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>TBF</strong></TableCell>
                  <TableCell>140-279 pts</TableCell>
                  <TableCell>Contender</TableCell>
                  <TableCell>Tantalus Boxing Federation - International liaison for cross-federation events and regulations</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>TBC</strong></TableCell>
                  <TableCell>280-559 pts</TableCell>
                  <TableCell>Elite</TableCell>
                  <TableCell>Tantalus Boxing Council - Advisory council for rules, safety standards, and judging criteria</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>TRM</strong></TableCell>
                  <TableCell>560+ pts</TableCell>
                  <TableCell>Elite+</TableCell>
                  <TableCell>Tantalus Ring Magazine - Official rankings, features, and coverage of Tantalus-sanctioned bouts</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Sanction Status</Typography>
          <Typography variant="body2" paragraph>
            Each sanction displays one of three statuses:
          </Typography>
          <List>
            <ListItem>
              <Chip label="Active" size="small" sx={{ bgcolor: '#22c55e', color: 'white', mr: 1 }} />
              <ListItemText 
                primary="Active"
                secondary="You have enough points to join this sanction. Click 'Join' to become a member."
              />
            </ListItem>
            <ListItem>
              <Chip label="Pending" size="small" sx={{ bgcolor: '#f59e0b', color: 'white', mr: 1 }} />
              <ListItemText 
                primary="Pending"
                secondary="You're halfway through the previous tier. This sanction will unlock soon! You'll receive a notification when it becomes available."
              />
            </ListItem>
            <ListItem>
              <Chip label="Locked" size="small" sx={{ bgcolor: '#6b7280', color: 'white', mr: 1 }} />
              <ListItemText 
                primary="Locked"
                secondary="You need more points to unlock this sanction. Keep fighting to advance!"
              />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>How to Join</Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="1. Navigate to Home Page → Boxing Sanctions tab"
                secondary="View all available sanctions, their unlock status, and point requirements."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="2. Check your sanction status"
                secondary="Each sanction card shows if it's Active (green), Pending (orange), or Locked (gray)."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="3. Click 'Join' on Active sanctions"
                secondary="Only Active sanctions can be joined. Locked sanctions will show the required points."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="4. View your ranking"
                secondary="Click 'View Fighters' to see all members ranked within that sanction."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="5. Leave anytime"
                secondary="You can leave a sanction at any time if you no longer wish to participate."
              />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Notifications</Typography>
          <Typography variant="body2" paragraph>
            You'll automatically receive notifications when:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="A sanction becomes Pending"
                secondary="When you reach the halfway point of your current tier, the next sanction will show as 'Pending' and you'll be notified."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="A sanction becomes Active"
                secondary="When you unlock a new sanction by reaching the required points, you'll receive a notification that it's available to join."
              />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Sanction Rankings</Typography>
          <Typography variant="body2" paragraph>
            Fighters within each sanction are ranked based on the following criteria (in priority order):
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="1. Wins (Primary)"
                secondary="More wins = better rank"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="2. Losses (Secondary)"
                secondary="Fewer losses = better rank (when wins are equal)"
              />
            </ListItem>
          </List>
          <Alert severity="info" sx={{ mt: 2 }}>
            <strong>Note:</strong> Sanction rankings prioritize win-loss records to reward consistent performance. Points, tier, and demotions are still tracked but don't affect sanction rankings.
          </Alert>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Ranking Display</Typography>
          <Typography variant="body2" paragraph>
            When viewing fighters in a sanction, you'll see:
          </Typography>
          <List>
            <ListItem><ListItemText primary="Rank (#1, #2, etc.) - #1 ranked fighter highlighted in gold" /></ListItem>
            <ListItem><ListItemText primary="Fighter name and handle" /></ListItem>
            <ListItem><ListItemText primary="Tier (color-coded chips)" /></ListItem>
            <ListItem><ListItemText primary="Total points" /></ListItem>
            <ListItem><ListItemText primary="Win-Loss-Draw record" /></ListItem>
            <ListItem><ListItemText primary="Number of demotions" /></ListItem>
          </List>

          <Alert severity="info" sx={{ mt: 3 }}>
            <strong>Note:</strong> Rankings update in real-time as fighters compete. You can join multiple sanctions to compete across different governing bodies. Each sanction maintains independent rankings.
          </Alert>
        </AccordionDetails>
      </Accordion>

      {/* Callout/Rematch System */}
      <Accordion expanded={expanded === 'callout-rematch-system'} onChange={handleChange('callout-rematch-system')} id="callout-rematch-system">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2}>
            <SportsMma color="primary" />
            <Typography variant="h5" fontWeight="bold">Callout/Rematch System</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" paragraph>
            Request a rematch with a fighter you've previously faced.
          </Typography>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Eligibility:</Typography>
          <List>
            <ListItem><ListItemText primary="Prior opponent only." /></ListItem>
            <ListItem><ListItemText primary="Both fighters must have availability (no conflicts)." /></ListItem>
            <ListItem><ListItemText primary="Must be a fair match (same tier, similar ranking)." /></ListItem>
          </List>
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Process:</Typography>
          <List>
            <ListItem><ListItemText primary="1. Select prior opponent from history, send callout with optional message." /></ListItem>
            <ListItem><ListItemText primary="2. Target receives notification." /></ListItem>
            <ListItem><ListItemText primary="3. On acceptance, fight is scheduled as a Scheduled Rematch." /></ListItem>
            <ListItem><ListItemText primary="4. All standard scheduled‑fight rules apply." /></ListItem>
          </List>
          <Typography variant="body2" paragraph sx={{ mt: 2 }}>
            <strong>Fair Match Requirements:</strong> Same weight, same tier (or adjacent with consent), rankings in reasonable range, compatible timezones.
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* Code of Conduct */}
      <Accordion expanded={expanded === 'code-of-conduct'} onChange={handleChange('code-of-conduct')} id="code-of-conduct">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2}>
            <Gavel color="primary" />
            <Typography variant="h5" fontWeight="bold">Code of Conduct</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="h6" gutterBottom>Respect & Sportsmanship</Typography>
          <List>
            <ListItem><ListItemText primary="Respect all members regardless of tier/rank." /></ListItem>
            <ListItem><ListItemText primary="No cheating, exploiting, or unsportsmanlike conduct." /></ListItem>
            <ListItem><ListItemText primary="Submit accurate results." /></ListItem>
            <ListItem><ListItemText primary="Respond promptly to match requests and admin messages." /></ListItem>
          </List>
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Prohibited</Typography>
          <List>
            <ListItem><ListItemText primary="Harassment, discriminatory language, false reporting, ghosting scheduled fights, exploiting bugs/loopholes." /></ListItem>
          </List>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <strong>Consequences:</strong> Warnings, point deductions, temporary suspensions, or permanent bans for severe violations.
          </Alert>
        </AccordionDetails>
      </Accordion>

      {/* Demotion and Promotion System */}
      <Accordion expanded={expanded === 'demotion-promotion-system'} onChange={handleChange('demotion-promotion-system')} id="demotion-promotion-system">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2}>
            <ArrowUpward color="primary" />
            <Typography variant="h5" fontWeight="bold">Demotion and Promotion System</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" paragraph>
            <strong>Automatic Promotion:</strong> Points‑based; triggered immediately on reaching the next tier's threshold.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>No Manual Promotion:</strong> Promotions are automatic.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Demotion Protection:</strong> You cannot drop a tier due to points loss alone.
          </Typography>
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Demotion (Consecutive Losses):</Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>⚠️ Warning Indicator:</strong> Fighters with 3+ consecutive losses are at risk of demotion. Look for the red warning indicator (!) next to fighters' names.
            </Typography>
          </Alert>
          <List>
            <ListItem>
              <Cancel color="error" sx={{ mr: 1 }} />
              <ListItemText primary="5 straight losses → demoted one tier." />
            </ListItem>
            <ListItem><ListItemText primary="Demotion is immediate after the 5th loss." /></ListItem>
            <ListItem><ListItemText primary="You keep your points but move to the lower tier." /></ListItem>
            <ListItem><ListItemText primary="Cannot demote below Amateur tier." /></ListItem>
          </List>
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Promotion After Demotion:</Typography>
          <List>
            <ListItem>
              <CheckCircle color="success" sx={{ mr: 1 }} />
              <ListItemText primary="5 straight wins → promoted back according to points; you cannot exceed the tier your points warrant." />
            </ListItem>
          </List>
        </AccordionDetails>
      </Accordion>

      {/* Fight Scheduling Rules */}
      <Accordion expanded={expanded === 'fight-scheduling-rules'} onChange={handleChange('fight-scheduling-rules')} id="fight-scheduling-rules">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2}>
            <Schedule color="primary" />
            <Typography variant="h5" fontWeight="bold">Fight Scheduling Rules</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="h6" gutterBottom>Mandatory Fights:</Typography>
          <List>
            <ListItem><ListItemText primary="Scheduled within 1 week of assignment and completed within 7 days." /></ListItem>
            <ListItem><ListItemText primary="Agree on date/time/platform; coordinate timezones." /></ListItem>
          </List>
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Scheduled Fights (non‑mandatory):</Typography>
          <List>
            <ListItem><ListItemText primary="May be set up to 4 weeks in advance." /></ListItem>
            <ListItem><ListItemText primary="Must include: date/time (with timezone), platform (PC/Xbox/PlayStation), connection notes, and any house rules." /></ListItem>
          </List>
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>After the Fight:</Typography>
          <List>
            <ListItem><ListItemText primary="1. Both fighters submit results." /></ListItem>
            <ListItem><ListItemText primary="2. Results must match." /></ListItem>
            <ListItem><ListItemText primary="3. If not, a dispute is opened." /></ListItem>
            <ListItem><ListItemText primary="4. Points are applied automatically after verification." /></ListItem>
            <ListItem><ListItemText primary="5. Records update and rankings recalc." /></ListItem>
          </List>
          <Typography variant="body2" paragraph sx={{ mt: 2 }}>
            <strong>Disputes:</strong> Must be filed within 48 hours of the scheduled fight time. Admins review evidence and decide.
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* General Guidelines */}
      <Accordion expanded={expanded === 'general-guidelines'} onChange={handleChange('general-guidelines')} id="general-guidelines">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2}>
            <People color="primary" />
            <Typography variant="h5" fontWeight="bold">General Guidelines</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="h6" gutterBottom>Getting Started:</Typography>
          <List>
            <ListItem><ListItemText primary="1. Complete your fighter profile (stats, trainer, gym, platform, timezone)." /></ListItem>
            <ListItem><ListItemText primary="2. Choose your weight class (changes are limited)." /></ListItem>
            <ListItem><ListItemText primary="3. Begin with mandatory fights or find opponents via matchmaking." /></ListItem>
          </List>
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Best Practices:</Typography>
          <List>
            <ListItem><ListItemText primary="New Fighters: Focus on learning in Amateur; fill your profile; enter eligible tournaments; build steadily." /></ListItem>
            <ListItem><ListItemText primary="Experienced Fighters: Maintain rank via consistent activity; use camps; mentor newcomers." /></ListItem>
            <ListItem><ListItemText primary="Everyone: Keep profiles up to date; respond quickly; report results accurately; follow announcements." /></ListItem>
          </List>
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Tips for Success:</Typography>
          <Typography variant="body2" paragraph>
            Consistency, fair matchups, tournament participation, training camps, positive community engagement.
          </Typography>
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Support:</Typography>
          <List>
            <ListItem><ListItemText primary="Read this guide first." /></ListItem>
            <ListItem><ListItemText primary="Contact admins for disputes or technical issues." /></ListItem>
            <ListItem><ListItemText primary="Watch the News feed for updates." /></ListItem>
          </List>
        </AccordionDetails>
      </Accordion>

      {/* Matchmaking Rules */}
      <Accordion expanded={expanded === 'matchmaking-rules'} onChange={handleChange('matchmaking-rules')} id="matchmaking-rules">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2}>
            <SportsMma color="primary" />
            <Typography variant="h5" fontWeight="bold">Matchmaking Rules</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" paragraph>
            The <strong>Smart Matchmaking</strong> system ensures competitive parity.
          </Typography>
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Mandatory Fights (Auto‑Matched)</Typography>
          <Typography variant="body2" paragraph sx={{ mb: 2 }}>
            <strong>All must be true:</strong>
          </Typography>
          <List>
            <ListItem>
              <CheckCircle color="success" sx={{ mr: 1 }} />
              <ListItemText primary="Same Weight Class" />
            </ListItem>
            <ListItem>
              <CheckCircle color="success" sx={{ mr: 1 }} />
              <ListItemText primary="Same Tier" />
            </ListItem>
            <ListItem>
              <CheckCircle color="success" sx={{ mr: 1 }} />
              <ListItemText primary="Rank within 3 positions" />
            </ListItem>
            <ListItem>
              <CheckCircle color="success" sx={{ mr: 1 }} />
              <ListItemText primary="Points within 30 points" />
            </ListItem>
            <ListItem>
              <CheckCircle color="success" sx={{ mr: 1 }} />
              <ListItemText primary="Timezones within 4 hours" />
            </ListItem>
            <ListItem>
              <CheckCircle color="success" sx={{ mr: 1 }} />
              <ListItemText primary="No Recent Opponents (avoids last 5)" />
            </ListItem>
          </List>
          <Typography variant="body2" paragraph sx={{ mt: 2 }}>
            <strong>Process:</strong> System generates pairing and notifies both fighters. Mandatory fight date is set <strong>one week</strong> from assignment. Bout must be completed within <strong>7 days</strong> of scheduling.
          </Typography>
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Manual Matchmaking (Weekly Mandatory Opponent Selection)</Typography>
          <List>
            <ListItem><ListItemText primary="Choose a suggested opponent who meets the same criteria." /></ListItem>
            <ListItem><ListItemText primary="Opponent must accept before scheduling." /></ListItem>
            <ListItem><ListItemText primary="You cannot accept your own request." /></ListItem>
            <ListItem><ListItemText primary="Once both accept, the fight is scheduled and binding." /></ListItem>
          </List>
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Fair Match Guarantees</Typography>
          <List>
            <ListItem><ListItemText primary="Points Gap Protection: If difference > 50 points, explicit consent from both fighters required." /></ListItem>
            <ListItem><ListItemText primary="Tier Protection: Matches occur within the same tier." /></ListItem>
            <ListItem><ListItemText primary="Rank Protection: Ranks must be within 3 positions." /></ListItem>
            <ListItem><ListItemText primary="Timezone Compatibility: Required for scheduling." /></ListItem>
          </List>
        </AccordionDetails>
      </Accordion>

      {/* Points System */}
      <Accordion expanded={expanded === 'points-system'} onChange={handleChange('points-system')} id="points-system">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2}>
            <EmojiEvents color="primary" />
            <Typography variant="h5" fontWeight="bold">Points System</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" paragraph>
            Every official fight affects your total.
          </Typography>
          <TableContainer component={Paper} sx={{ mt: 2, mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Result</strong></TableCell>
                  <TableCell><strong>Points</strong></TableCell>
                  <TableCell><strong>Notes</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell><strong>Win</strong></TableCell>
                  <TableCell>+5</TableCell>
                  <TableCell>Base points for victory</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Win by KO/TKO</strong></TableCell>
                  <TableCell>+8</TableCell>
                  <TableCell>+5 base +3 KO bonus</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Loss</strong></TableCell>
                  <TableCell>−3</TableCell>
                  <TableCell>Deduction for defeat</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Draw</strong></TableCell>
                  <TableCell>0</TableCell>
                  <TableCell>No change</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Alert severity="info" sx={{ mt: 3, mb: 2 }}>
            <Typography variant="body2" component="div">
              <strong>Automatic Opponent Loss System:</strong> When you enter a fight record as a Win, your opponent automatically receives a Loss record with -3 points. Both fighters' rankings, demotions, tier, and points update in real-time across all systems (Home Page, Rankings, Weight Class, Demotion System, Tier System, Smart Matchmaking, and Analytics).
            </Typography>
          </Alert>
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Notes</Typography>
          <List>
            <ListItem>
              <ListItemText primary="Both fighters' records are updated automatically after each bout." />
            </ListItem>
            <ListItem>
              <ListItemText primary="When you enter a Win, your opponent automatically receives a Loss (-3 points)." />
            </ListItem>
            <ListItem>
              <ListItemText primary="KO/TKO bonus applies only to the winner." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Points are calculated automatically upon verified result submission." />
            </ListItem>
            <ListItem>
              <ListItemText primary="All systems update in real-time: Home Page, Rankings, Weight Class, Demotion System, Tier System, Smart Matchmaking, and Analytics." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Full point history appears in each fighter's record." />
            </ListItem>
          </List>
        </AccordionDetails>
      </Accordion>

      {/* Rankings System */}
      <Accordion expanded={expanded === 'rankings-system'} onChange={handleChange('rankings-system')} id="rankings-system">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2}>
            <TrendingUp color="primary" />
            <Typography variant="h5" fontWeight="bold">Rankings System</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" paragraph>
            Rank determines position within weight class, tier, and overall club.
          </Typography>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Ranking Factors (priority order):</Typography>
          <List>
            <ListItem><ListItemText primary="1. Total Points" /></ListItem>
            <ListItem><ListItemText primary="2. Head‑to‑Head Record" /></ListItem>
            <ListItem><ListItemText primary="3. KO%" /></ListItem>
            <ListItem><ListItemText primary="4. Strength of Opponent (average points of opponents)" /></ListItem>
            <ListItem><ListItemText primary="5. Recent Form (last 5 fights)" /></ListItem>
            <ListItem><ListItemText primary="6. Win %" /></ListItem>
          </List>
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Ranking Types:</Typography>
          <List>
            <ListItem><ListItemText primary="Weight Class Rankings (division specific)" /></ListItem>
            <ListItem><ListItemText primary="Overall Rankings (cross‑division)" /></ListItem>
            <ListItem><ListItemText primary="Tier Rankings (within your current tier)" /></ListItem>
          </List>
          <Alert severity="info" sx={{ mt: 2 }}>
            Rankings update after each verified result and are reconciled daily.
          </Alert>
        </AccordionDetails>
      </Accordion>

      {/* Record Your Fights */}
      <Accordion expanded={expanded === 'record-your-fights'} onChange={handleChange('record-your-fights')} id="record-your-fights">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2}>
            <Schedule color="primary" />
            <Typography variant="h5" fontWeight="bold">Record Your Fights</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="h6" gutterBottom>Purpose</Typography>
          <Typography variant="body1" paragraph>
            To ensure fairness, transparency, and accurate scoring in all Tantalus Boxing Club events—including live events, ranked matches, and tournaments—all fighters are required to record their fights and submit their footage for review.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>1. Mandatory Recording Requirements</Typography>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 2, fontSize: '1.1rem' }}>1.1 All Fighters Must Record</Typography>
          <Typography variant="body2" paragraph>
            Every fighter must record every fight from start to finish.
          </Typography>
          <Typography variant="body2" paragraph sx={{ mb: 2 }}>
            Recordings are required for:
          </Typography>
          <List>
            <ListItem><ListItemText primary="Live events" /></ListItem>
            <ListItem><ListItemText primary="Tournament fights" /></ListItem>
            <ListItem><ListItemText primary="Ranked or club-sanctioned matches" /></ListItem>
            <ListItem><ListItemText primary="Any match requested by an Admin" /></ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3, fontSize: '1.1rem' }}>1.2 What Must Be Included in the Recording</Typography>
          <List>
            <ListItem><ListItemText primary="The full fight with no cuts or interruptions" /></ListItem>
            <ListItem><ListItemText primary="Clear gameplay visuals" /></ListItem>
            <ListItem><ListItemText primary="Game audio enabled" /></ListItem>
            <ListItem><ListItemText primary="Microphone audio muted" /></ListItem>
            <ListItem><ListItemText primary="Scorecard screenshot after the match (see Section 3)" /></ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>2. Audio & Video Quality Standards</Typography>
          <Typography variant="body2" paragraph sx={{ mb: 2 }}>
            To ensure the Admins can properly review your fight:
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2, fontSize: '1.1rem' }}>2.1 Microphones Must Be Muted</Typography>
          <List>
            <ListItem><ListItemText primary="Personal microphone audio must be OFF during recording." /></ListItem>
            <ListItem><ListItemText primary="No party chat, voice chat, or background conversation may be included." /></ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3, fontSize: '1.1rem' }}>2.2 Game Audio Must Be ON</Typography>
          <List>
            <ListItem><ListItemText primary="Game sounds must be audible in the recording." /></ListItem>
            <ListItem><ListItemText primary="Audio cannot be overly quiet, distorted, or drowned out by noise." /></ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3, fontSize: '1.1rem' }}>2.3 Video Must Be Clear</Typography>
          <List>
            <ListItem><ListItemText primary="Minimum resolution: 720p" /></ListItem>
            <ListItem><ListItemText primary="Recommended: 1080p or higher" /></ListItem>
            <ListItem><ListItemText primary="Screen must be fully visible (no shaky phone recordings of a TV)." /></ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3, fontSize: '1.1rem' }}>2.4 Audio Must Be Clear</Typography>
          <List>
            <ListItem><ListItemText primary="Game audio must be clean and clearly captured." /></ListItem>
            <ListItem><ListItemText primary="No added commentary or music overlays." /></ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>3. Fight Submission Requirements</Typography>
          <Typography variant="body2" paragraph sx={{ mb: 2 }}>
            To submit an official fight for review:
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2, fontSize: '1.1rem' }}>3.1 Upload the Fight Recording</Typography>
          <Typography variant="body2" paragraph sx={{ mb: 1 }}>
            Acceptable platforms:
          </Typography>
          <List>
            <ListItem><ListItemText primary="YouTube" /></ListItem>
            <ListItem><ListItemText primary="Twitch" /></ListItem>
            <ListItem><ListItemText primary="Facebook Gaming" /></ListItem>
            <ListItem><ListItemText primary="Google Drive" /></ListItem>
            <ListItem><ListItemText primary="OneDrive" /></ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3, fontSize: '1.1rem' }}>3.2 Submit the Following to Admin</Typography>
          <Typography variant="body2" paragraph sx={{ mb: 1, fontWeight: 'bold' }}>
            You MUST send both:
          </Typography>
          <List>
            <ListItem><ListItemText primary="The fight URL/Web link" /></ListItem>
            <ListItem><ListItemText primary="A screenshot of your final scorecard" /></ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3, fontSize: '1.1rem' }}>3.3 Review & Approval</Typography>
          <List>
            <ListItem><ListItemText primary="Admins will review the video and scorecard." /></ListItem>
            <ListItem><ListItemText primary="Submissions may be rejected if:" /></ListItem>
          </List>
          <Box sx={{ pl: 4, mb: 2 }}>
            <List dense>
              <ListItem><ListItemText primary="Video is missing or incomplete" /></ListItem>
              <ListItem><ListItemText primary="Mic is not muted" /></ListItem>
              <ListItem><ListItemText primary="Game audio is off" /></ListItem>
              <ListItem><ListItemText primary="Video or audio is unclear" /></ListItem>
              <ListItem><ListItemText primary="Scorecard screenshot is missing" /></ListItem>
            </List>
          </Box>
          <Typography variant="body2" paragraph>
            Only Admin-approved fights count as official results.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>4. How to Record Your Video Game Screen</Typography>
          <Typography variant="body2" paragraph sx={{ mb: 2 }}>
            Below are platform-specific instructions for recording gameplay.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2, fontSize: '1.1rem' }}>4.1 Recording on Xbox (Xbox One, Series S/X)</Typography>
          
          <Typography variant="body2" paragraph sx={{ mt: 2, fontWeight: 'bold' }}>A. Start Recording</Typography>
          <List>
            <ListItem><ListItemText primary="Press the Xbox button." /></ListItem>
            <ListItem><ListItemText primary="Go to Capture & Share." /></ListItem>
            <ListItem><ListItemText primary="Select Start Recording." /></ListItem>
            <ListItem><ListItemText primary="Make sure your mic is muted and game audio is enabled." /></ListItem>
            <ListItem><ListItemText primary="Press the Xbox button and choose Stop Recording when finished." /></ListItem>
          </List>

          <Typography variant="body2" paragraph sx={{ mt: 2, fontWeight: 'bold' }}>B. Upload the Recording</Typography>
          <List>
            <ListItem><ListItemText primary="Open Captures → select the clip → Share." /></ListItem>
            <ListItem><ListItemText primary="Share to OneDrive (for PC upload), YouTube, or a connected account." /></ListItem>
          </List>

          <Typography variant="body2" paragraph sx={{ mt: 2, fontWeight: 'bold' }}>C. For Twitch Streaming</Typography>
          <List>
            <ListItem><ListItemText primary="Install Twitch app on Xbox." /></ListItem>
            <ListItem><ListItemText primary="Go to Broadcast." /></ListItem>
            <ListItem><ListItemText primary="Turn Microphone Off." /></ListItem>
            <ListItem><ListItemText primary="Start streaming—VODs save automatically if enabled." /></ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3, fontSize: '1.1rem' }}>4.2 Recording on PlayStation (PS4 & PS5)</Typography>
          
          <Typography variant="body2" paragraph sx={{ mt: 2, fontWeight: 'bold' }}>A. Prepare Settings</Typography>
          <List>
            <ListItem><ListItemText primary="Press the Create (PS5) or Share (PS4) button." /></ListItem>
            <ListItem><ListItemText primary="Go to Capture Settings." /></ListItem>
            <ListItem><ListItemText primary="Set quality to 1080p (recommended)." /></ListItem>
            <ListItem><ListItemText primary="Ensure microphone audio is muted." /></ListItem>
          </List>

          <Typography variant="body2" paragraph sx={{ mt: 2, fontWeight: 'bold' }}>B. Start Recording</Typography>
          <List>
            <ListItem><ListItemText primary="Press Create/Share." /></ListItem>
            <ListItem><ListItemText primary="Select Start Recording." /></ListItem>
            <ListItem><ListItemText primary="Play your fight." /></ListItem>
            <ListItem><ListItemText primary="Press the button again → Stop Recording." /></ListItem>
          </List>

          <Typography variant="body2" paragraph sx={{ mt: 2, fontWeight: 'bold' }}>C. Uploading</Typography>
          <List>
            <ListItem><ListItemText primary="Go to Media Gallery → choose clip → Share to:" /></ListItem>
          </List>
          <Box sx={{ pl: 4, mb: 2 }}>
            <List dense>
              <ListItem><ListItemText primary="YouTube" /></ListItem>
              <ListItem><ListItemText primary="Twitter" /></ListItem>
              <ListItem><ListItemText primary="USB for PC upload" /></ListItem>
            </List>
          </Box>

          <Typography variant="body2" paragraph sx={{ mt: 2, fontWeight: 'bold' }}>D. Streaming on Twitch/YouTube</Typography>
          <List>
            <ListItem><ListItemText primary="Press Create/Share → Broadcast." /></ListItem>
            <ListItem><ListItemText primary="Select Twitch/YouTube." /></ListItem>
            <ListItem><ListItemText primary="Set mic to Mute." /></ListItem>
            <ListItem><ListItemText primary="Start live streaming." /></ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3, fontSize: '1.1rem' }}>4.3 Recording on PC</Typography>
          
          <Typography variant="body2" paragraph sx={{ mt: 2, fontWeight: 'bold' }}>Option A: Windows Game Bar (Win 10/11)</Typography>
          <List>
            <ListItem><ListItemText primary="Press Windows + G." /></ListItem>
            <ListItem><ListItemText primary="Select Capture widget." /></ListItem>
            <ListItem><ListItemText primary="Mute microphone in capture settings." /></ListItem>
            <ListItem><ListItemText primary="Click Start Recording." /></ListItem>
            <ListItem><ListItemText primary="Press Win + Alt + R to stop." /></ListItem>
          </List>

          <Typography variant="body2" paragraph sx={{ mt: 2, fontWeight: 'bold' }}>Option B: OBS Studio</Typography>
          <List>
            <ListItem><ListItemText primary="Install OBS Studio." /></ListItem>
            <ListItem><ListItemText primary="Add Game Capture or Display Capture source." /></ListItem>
            <ListItem><ListItemText primary="Mute Microphone/Aux." /></ListItem>
            <ListItem><ListItemText primary="Keep Desktop Audio ON." /></ListItem>
            <ListItem><ListItemText primary="Click Start Recording → Stop Recording when done." /></ListItem>
          </List>

          <Typography variant="body2" paragraph sx={{ mt: 2, fontWeight: 'bold' }}>Option C: Uploading</Typography>
          <Typography variant="body2" paragraph sx={{ mb: 1 }}>
            Upload the video to:
          </Typography>
          <List>
            <ListItem><ListItemText primary="YouTube" /></ListItem>
            <ListItem><ListItemText primary="Twitch (as VOD)" /></ListItem>
            <ListItem><ListItemText primary="Facebook Gaming" /></ListItem>
            <ListItem><ListItemText primary="Google Drive" /></ListItem>
            <ListItem><ListItemText primary="OneDrive" /></ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>5. Posting & Saving on Twitch, Facebook, or YouTube</Typography>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 2, fontSize: '1.1rem' }}>5.1 Twitch</Typography>
          <List>
            <ListItem><ListItemText primary="Enable Store Past Broadcasts (Settings → Stream → VOD Settings)." /></ListItem>
            <ListItem><ListItemText primary="Videos appear under Video Producer for sharing." /></ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3, fontSize: '1.1rem' }}>5.2 YouTube</Typography>
          <List>
            <ListItem><ListItemText primary="Upload your recorded file." /></ListItem>
            <ListItem><ListItemText primary="Set visibility to Unlisted (recommended for Admin review)." /></ListItem>
            <ListItem><ListItemText primary="Copy link & submit." /></ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3, fontSize: '1.1rem' }}>5.3 Facebook Gaming</Typography>
          <List>
            <ListItem><ListItemText primary="Upload video via Create Post → Add Video." /></ListItem>
            <ListItem><ListItemText primary="Ensure the video is fully processed before copying the link." /></ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>6. Policy Enforcement</Typography>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 2, fontSize: '1.1rem' }}>6.1 Failure to Comply</Typography>
          <Typography variant="body2" paragraph sx={{ mb: 1 }}>
            A fighter may face:
          </Typography>
          <List>
            <ListItem><ListItemText primary="Fight invalidation" /></ListItem>
            <ListItem><ListItemText primary="Disqualification" /></ListItem>
            <ListItem><ListItemText primary="Removal from tournament brackets" /></ListItem>
            <ListItem><ListItemText primary="Temporary or permanent club sanctions" /></ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3, fontSize: '1.1rem' }}>6.2 Prohibited</Typography>
          <List>
            <ListItem><ListItemText primary="Edited or tampered footage" /></ListItem>
            <ListItem><ListItemText primary="Intentional audio/video manipulation" /></ListItem>
            <ListItem><ListItemText primary="Missing scorecard screenshots" /></ListItem>
            <ListItem><ListItemText primary="Incomplete recordings" /></ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>7. Acknowledgment</Typography>
          <Typography variant="body1" paragraph>
            By participating in any Tantalus Boxing Club event, all fighters agree to abide by this Recording Policy. Non-compliance may result in disciplinary action at Admin discretion.
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* Simulation Virtual Boxing Standard (Undisputed) */}
      <Accordion expanded={expanded === 'simulation-standard'} onChange={handleChange('simulation-standard')} id="simulation-standard">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2}>
            <Gavel color="primary" />
            <Typography variant="h5" fontWeight="bold">Simulation Virtual Boxing Standard (Undisputed)</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" paragraph>
            This section defines <strong>simulation</strong> for Undisputed matches in TBC‑CFC and integrates directly with club operations.
          </Typography>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>1) Philosophy & Definition</Typography>
          <Typography variant="body2" paragraph>
            Sim means boxing to real‑fight principles—not exploiting game systems.
          </Typography>
          <List>
            <ListItem><ListItemText primary="Pace realistically and manage stamina; build behind the jab." /></ListItem>
            <ListItem><ListItemText primary="Use varied offense/defense; clean punching over volume spam." /></ListItem>
            <ListItem><ListItemText primary="Show ring generalship; do not 'run' to avoid exchanges." /></ListItem>
            <ListItem><ListItemText primary="Accept realistic outcomes (cuts, attrition, momentum shifts)." /></ListItem>
          </List>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <strong>Non‑Sim Examples:</strong> Punch spam (especially body spam), macro/turbo use, perpetual backpedal with no engagement, repeated push/clinch spam to stall, or any exploit/bug use.
          </Alert>

          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>2) Match Formats & Setup (Host Settings)</Typography>
          <List>
            <ListItem><ListItemText primary="Bout Types: Amateur 4–6 | Exhibition 6–8 | Contender/Main 8–10 | Championship 10–12." /></ListItem>
            <ListItem><ListItemText primary="Game Type: Simulation (or closest online equivalent)" /></ListItem>
            <ListItem><ListItemText primary="Round Length: 3:00 (Relative Time = 1.00)" /></ListItem>
            <ListItem><ListItemText primary="Knockdown Limit: 3 (3 KD Rule)" /></ListItem>
            <ListItem><ListItemText primary="Saved by the Bell: Off" /></ListItem>
            <ListItem><ListItemText primary="Referee Leniency: 3.0" /></ListItem>
            <ListItem><ListItemText primary="Maximum Penalties: 2.0" /></ListItem>
            <ListItem><ListItemText primary="Cuts/Injuries: On" /></ListItem>
            <ListItem><ListItemText primary="Fouls: On" /></ListItem>
            <ListItem><ListItemText primary="Judging: 10‑Point Must" /></ListItem>
            <ListItem><ListItemText primary="HUD/Camera: Minimal HUD preferred; default camera only unless pre‑agreed" /></ListItem>
            <ListItem><ListItemText primary="Connectivity: Stable connection required; ref/commissioner may order restart/reschedule for latency or desync." /></ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>3) Weight Classes & Fighter Selection</Typography>
          <List>
            <ListItem><ListItemText primary="Sanctioned bouts must be same weight class; catchweights require mutual consent and pre‑announcement." /></ListItem>
            <ListItem><ListItemText primary="Mirror matches discouraged for title fights; coin‑toss/draft order resolves conflicts." /></ListItem>
            <ListItem><ListItemText primary="Fighters disclose controller type; macros/turbos are banned." /></ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Created Fighter (CAF) Policy (Club Caps)</Typography>
          <Typography variant="body2" paragraph>
            To keep CAFs fair and believable:
          </Typography>
          <List>
            <ListItem><ListItemText primary="Attribute Budget Cap: Club‑set total budget per CAF." /></ListItem>
            <ListItem><ListItemText primary="All Creative Fighters Overall must be 85." /></ListItem>
            <ListItem><ListItemText primary="No Traits." /></ListItem>
            <ListItem><ListItemText primary="Hard Caps: Max 2 attributes > 90; no other stat > 89" /></ListItem>
            <ListItem><ListItemText primary="Body Metrics: Height/reach must be plausible for the weight class; no extreme body types to evade hitboxes." /></ListItem>
            <ListItem><ListItemText primary="Cosmetics: No distracting/glitch‑triggering gear." /></ListItem>
            <ListItem><ListItemText primary="Audit: Submit CAF screenshots for approval before first sanctioned bout." /></ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>4) Simulation Standards (In‑Ring Conduct)</Typography>
          <List>
            <ListItem><ListItemText primary="Pacing & Output: Aim ~35–70 punches/round on average; sustained >90/rd over multiple rounds is a spam flag." /></ListItem>
            <ListItem><ListItemText primary="Shot Selection: At least 20% jabs across the fight; mix straights/hooks/uppercuts; avoid repeating the same power shot >3 times consecutively." /></ListItem>
            <ListItem><ListItemText primary="Targeting Balance: No more than 70% to head or body over the whole bout without clear tactical reason." /></ListItem>
            <ListItem><ListItemText primary="Engagement: Lateral movement ok; repeatedly sprinting corner‑to‑corner without throwing (>15s, twice in a round) is running → warning." /></ListItem>
            <ListItem><ListItemText primary="Clinch/Push: Use to recover or reset only; >5 neutral clinches/round (while not hurt) or push‑spam → penalties." /></ListItem>
            <ListItem><ListItemText primary="Power Use: 2–4 shot combos typical; power‑modifier reserved for set‑ups/openings." /></ListItem>
            <ListItem><ListItemText primary="Sportsmanship: No taunt spam, glove‑touch fakes into power shots, or post‑bell punches." /></ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>5) Prohibited Tactics & Exploits</Typography>
          <List>
            <ListItem><ListItemText primary="Repetitive same‑punch spam (e.g., hook‑hook‑hook‑hook), nonstop body jab/uppercut spam, jab‑dash loops." /></ListItem>
            <ListItem><ListItemText primary="Stalling through non‑engagement, corner‑escape loops, clinch/push spam." /></ListItem>
            <ListItem><ListItemText primary="Any exploit/bug or disconnect/no‑loss trick; collision/hitbox abuse." /></ListItem>
            <ListItem><ListItemText primary="Macros/turbos/scripts and rapid‑fire hardware/software." /></ListItem>
          </List>
          <Alert severity="error" sx={{ mt: 2 }}>
            <strong>Penalty Ladder:</strong> Verbal Warning → Official Warning (1‑point deduction) → DQ → Suspension
          </Alert>

          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>6) Judging & Scoring</Typography>
          <List>
            <ListItem><ListItemText primary="Three judges; 10‑Point Must each round." /></ListItem>
            <ListItem><ListItemText primary="Criteria: Clean punching, effective aggression, ring generalship, defense." /></ListItem>
            <ListItem><ListItemText primary="Knockdowns and point deductions applied per rules." /></ListItem>
            <ListItem><ListItemText primary="Commission may review/overturn if the in‑game decision contradicts sim standards." /></ListItem>
          </List>
          <Typography variant="body2" paragraph sx={{ mt: 2 }}>
            <strong>Post‑Fight Stats (recommended):</strong> Submit screenshots (total/jab/power thrown/landed, head/body split, stamina) with cards for audits.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>7) Refereeing & Administration</Typography>
          <List>
            <ListItem><ListItemText primary="Pre‑fight: Ref validates settings, rounds, division, and connection quality." /></ListItem>
            <ListItem><ListItemText primary="During fight: Issue warnings for non‑sim conduct; one pause per fighter to resolve latency/input issues." /></ListItem>
            <ListItem><ListItemText primary="Medical: Cuts/swelling are part of sim; stop for TKO when warranted." /></ListItem>
            <ListItem><ListItemText primary="Cards: Judges keep round‑by‑round cards; ref collects and submits." /></ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>8) Integrity, Reporting & Appeals</Typography>
          <List>
            <ListItem><ListItemText primary="Save clips of key moments (KDs, fouls)." /></ListItem>
            <ListItem><ListItemText primary="Protests: Filed within 24 hours with timestamps/stats." /></ListItem>
            <ListItem><ListItemText primary="Appeals Panel: Commissioner + two neutral seniors; majority decision final." /></ListItem>
            <ListItem><ListItemText primary="Possible sanctions: point deductions, No Contest, suspensions, rank forfeits." /></ListItem>
          </List>
        </AccordionDetails>
      </Accordion>

      {/* Tantalus Ring Magazine Media */}
      <Accordion expanded={expanded === 'tantalus-ring-media'} onChange={handleChange('tantalus-ring-media')} id="tantalus-ring-media">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2}>
            <Share color="primary" />
            <Typography variant="h5" fontWeight="bold">Tantalus Ring Magazine Media</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" paragraph>
            The <strong>Tantalus Ring Magazine Media</strong> feature allows fighters to create and share their public media profiles, showcasing their social media presence, fight records, and creative fighter images to fans, sponsors, and the community.
          </Typography>

          <Alert severity="info" sx={{ mt: 2, mb: 3 }}>
            <strong>New in v1.1.2:</strong> Create your media profile, add social media links, and share your public profile with the world!
          </Alert>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Creating Your Media Profile</Typography>
          <Typography variant="body2" paragraph>
            To set up your Tantalus Ring Magazine Media profile:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="1. Navigate to My Profile → Physical Information section"
                secondary="Click 'Edit' to enter edit mode."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="2. Add Your Social Media Bio"
                secondary="Enter a short bio describing yourself, your fighting style, achievements, or anything you want fans to know. This appears on your public media profile."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="3. Add Social Media Links"
                secondary="Click 'Add' to add links to your social media platforms. Supported platforms include: Twitter, Instagram, YouTube, Twitch, TikTok, and Facebook. You can add multiple links and include an optional handle (e.g., @yourhandle)."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="4. Save Your Changes"
                secondary="Click 'Save' to update your profile. Your media profile will automatically appear in the Tantalus Ring Magazine Media Following channel once you've added a bio."
              />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Shareable Media Profile</Typography>
          <Typography variant="body2" paragraph>
            Every fighter with a media profile gets a unique, shareable link:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Your Media Link"
                secondary="Format: /media/{'{your_user_id}'} - This link is unique to your fighter profile."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Copy Shareable Link"
                secondary="In your profile, click 'Copy Shareable Link' to copy your media profile URL to your clipboard. Share this link with fans, sponsors, or on social media."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Public Access"
                secondary="Anyone with your link can view your media profile without logging in. Perfect for sharing with external audiences!"
              />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>What's Displayed on Your Media Profile</Typography>
          <Typography variant="body2" paragraph>
            Your public media profile showcases:
          </Typography>
          <List>
            <ListItem><ListItemText primary="Fighter name, handle, tier, and points" /></ListItem>
            <ListItem><ListItemText primary="Fight record (Wins-Losses-Draws) and knockouts" /></ListItem>
            <ListItem><ListItemText primary="Social media bio" /></ListItem>
            <ListItem><ListItemText primary="Social media links (clickable)" /></ListItem>
            <ListItem><ListItemText primary="Creative Fighter image (if uploaded)" /></ListItem>
            <ListItem><ListItemText primary="Recent fight records" /></ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Tantalus Ring Magazine Media Following</Typography>
          <Typography variant="body2" paragraph>
            Discover other fighters with media profiles:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="1. Navigate to Social Page"
                secondary="Go to the Social page in the main navigation."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="2. Open Media Following Tab"
                secondary="Click on the 'Tantalus Ring Magazine Media Following' tab (second tab)."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="3. Browse Fighters"
                secondary="View all fighters who have set up their media profiles. Fighters are sorted by points (highest first)."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="4. View Profiles"
                secondary="Click 'View Profile' on any fighter card to open their full media profile in a new tab."
              />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Managing Your Social Media Links</Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Adding Links"
                secondary="Select a platform, enter the URL, and optionally add a handle. Click 'Add' to save."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Removing Links"
                secondary="Click the delete icon next to any link to remove it from your profile."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Editing Your Bio"
                secondary="Go to edit mode in My Profile → Physical Information and update your bio text. Save to apply changes."
              />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Best Practices</Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Keep Your Bio Updated"
                secondary="Regularly update your bio to reflect your latest achievements, upcoming fights, or career highlights."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Add All Your Social Media"
                secondary="Include links to all your active social media accounts to maximize your reach and engagement."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Share Your Profile"
                secondary="Share your media link on your social media accounts, in fight announcements, or with potential sponsors."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Upload Creative Fighter Image"
                secondary="Make sure you've uploaded your Creative Fighter image in My Profile to showcase it on your media profile."
              />
            </ListItem>
          </List>

          <Alert severity="info" sx={{ mt: 3 }}>
            <strong>Note:</strong> Your media profile only appears in the Media Following channel if you've added a social media bio. Fighters without a bio won't be listed, but you can still share your media link directly.
          </Alert>
        </AccordionDetails>
      </Accordion>

      {/* Tier System */}
      <Accordion expanded={expanded === 'tier-system'} onChange={handleChange('tier-system')} id="tier-system">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2}>
            <TrendingUp color="primary" />
            <Typography variant="h5" fontWeight="bold">Tier System</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" paragraph>
            All fighters start in <strong>Amateur</strong> and progress by earning points.
          </Typography>
          <TableContainer component={Paper} sx={{ mt: 2, mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Tier</strong></TableCell>
                  <TableCell><strong>Points Range</strong></TableCell>
                  <TableCell><strong>Benefits</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell><strong>Amateur</strong></TableCell>
                  <TableCell>0–29</TableCell>
                  <TableCell>Basic training access, local events</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Semi‑Pro</strong></TableCell>
                  <TableCell>30–69</TableCell>
                  <TableCell>Advanced training, regional events, basic analytics</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Pro</strong></TableCell>
                  <TableCell>70–139</TableCell>
                  <TableCell>Professional training, national events, full analytics, sponsorship opportunities</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Contender</strong></TableCell>
                  <TableCell>140–279</TableCell>
                  <TableCell>Elite training, championship events, advanced analytics, media coverage, title shots</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Elite</strong></TableCell>
                  <TableCell>280–559</TableCell>
                  <TableCell>World‑class training, global events, premium analytics, live streaming, interviews, championship belts</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Hall of famer</strong></TableCell>
                  <TableCell>560+</TableCell>
                  <TableCell>Legendary status, Hall of Fame recognition, all Elite benefits, exclusive events, lifetime achievements</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Tier Advancement</Typography>
          <List>
            <ListItem>
              <ListItemText primary="Automatic Promotion when you reach the next tier's minimum points." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Tier Benefits unlock immediately upon promotion." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Tier Lock: You cannot be demoted below your current tier due to points alone (see Demotion System)." />
            </ListItem>
          </List>
        </AccordionDetails>
      </Accordion>

      {/* Tournament Rules */}
      <Accordion expanded={expanded === 'tournament-rules'} onChange={handleChange('tournament-rules')} id="tournament-rules">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2}>
            <Sports color="primary" />
            <Typography variant="h5" fontWeight="bold">Tournament Rules</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="h6" gutterBottom>Eligibility (all required):</Typography>
          <List>
            <ListItem><ListItemText primary="1. Correct weight class (or approved adjacent class)." /></ListItem>
            <ListItem><ListItemText primary="2. Meet tournament minimum tier." /></ListItem>
            <ListItem><ListItemText primary="3. Meet minimum points (if listed)." /></ListItem>
            <ListItem><ListItemText primary="4. Meet minimum rank (if listed)." /></ListItem>
            <ListItem><ListItemText primary="5. Register before the posted deadline." /></ListItem>
          </List>
          <Typography variant="body2" paragraph sx={{ mt: 2 }}>
            <strong>Formats:</strong> Single Elimination, Double Elimination, Group Stage → Knockouts, Swiss, Round Robin.
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>Rules:</strong> Check‑in is required; no‑shows are eliminated; seeding uses current rankings; prize pools may be offered; entry fees may apply.
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* TBC Promotions Fight Calendar */}
      <Accordion expanded={expanded === 'tbc-promotions-fight-calendar'} onChange={handleChange('tbc-promotions-fight-calendar')} id="tbc-promotions-fight-calendar">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2}>
            <Event color="primary" />
            <Typography variant="h5" fontWeight="bold">TBC Promotions Fight Calendar</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" paragraph>
            The <strong>TBC Promotions Fight Calendar</strong> is the central hub for all official Tantalus Boxing Club events, including Fight Cards and Tournaments. All scheduling and fighter selections are managed by Administrators.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Fight Cards & Tournament Scheduling</Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Admin-Posted Schedules"
                secondary="The Admin will post Fight Cards and Tournament schedules on the TBC Promotions Fight Calendar. All upcoming events, dates, and details are published here for fighters to view."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Centralized Calendar"
                secondary="The Fight Calendar serves as the single source of truth for all official TBC events. Check regularly for updates and new event announcements."
              />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Fighter Selection for Live Events & Tournaments</Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Performance-Based Selection"
                secondary="The Admin will select fighters for Live events and Tournaments according to their Performance. Factors considered include: points, tier, win-loss record, recent form, and overall activity."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Selection Criteria"
                secondary="Fighters are selected based on their competitive performance, consistency, and standing within their weight class and tier. Active participation and strong records increase selection opportunities."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Fair Matchmaking"
                secondary="Admin ensures competitive and fair matchups by considering tier, points, and recent performance when creating Fight Cards and Tournament brackets."
              />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>News & Announcements</Typography>
          <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body2">
              <strong>All Admin Updates:</strong> All updates by the Admin will display in the News and Announcements section. This includes:
            </Typography>
          </Alert>
          <List>
            <ListItem>
              <ListItemText primary="New Fight Cards posted to the calendar" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Tournament announcements and registration details" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Fighter selections for Live events" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Schedule changes or updates" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Important club announcements and policy updates" />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>How to Stay Informed</Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Check the Fight Calendar regularly"
                secondary="Visit the TBC Promotions Fight Calendar to see all upcoming events and schedules."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Monitor News & Announcements"
                secondary="All Admin updates, event announcements, and fighter selections are posted in the News and Announcements feed. Check this section frequently for the latest information."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Maintain Strong Performance"
                secondary="Focus on building a strong record, maintaining activity, and performing well in your fights to increase your chances of being selected for Live events and Tournaments."
              />
            </ListItem>
          </List>
        </AccordionDetails>
      </Accordion>

      {/* Training Camp System */}
      <Accordion expanded={expanded === 'training-camp-system'} onChange={handleChange('training-camp-system')} id="training-camp-system">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2}>
            <FitnessCenter color="primary" />
            <Typography variant="h5" fontWeight="bold">Training Camp System</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" paragraph>
            Practice without record impact.
          </Typography>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Invites:</Typography>
          <List>
            <ListItem><ListItemText primary="Invite any fighter except scheduled or past opponents." /></ListItem>
            <ListItem><ListItemText primary="Up to 3 active sparring partners." /></ListItem>
            <ListItem><ListItemText primary="Camps last 72 hours from acceptance." /></ListItem>
          </List>
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Restrictions:</Typography>
          <List>
            <ListItem><ListItemText primary="Cannot start a camp within 3 days of a scheduled fight." /></ListItem>
            <ListItem><ListItemText primary="Both fighters must accept." /></ListItem>
            <ListItem><ListItemText primary="Camps expire after 72 hours." /></ListItem>
          </List>
          <Typography variant="body2" paragraph sx={{ mt: 2 }}>
            <strong>Benefits:</strong> Skill work, strategy testing, relationship building. No points are at stake.
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* Footer */}
      <Paper elevation={2} sx={{ p: 3, mt: 4, textAlign: 'center', bgcolor: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(10px)' }}>
        <Typography variant="body2" color="text.secondary">
          For questions or clarifications, contact club administrators or check the News feed for updates.
        </Typography>
      </Paper>
      </Container>
    </>
  );
};

// Rules/Guidelines component for Creative Fighter Club
export default RulesGuidelines;

